const getEnvValue = (key) => {
  if (typeof import.meta !== 'undefined' && import.meta.env && key in import.meta.env) {
    return import.meta.env[key]
  }

  if (typeof process !== 'undefined' && process.env && key in process.env) {
    return process.env[key]
  }

  return ''
}

const DEFAULT_BASE_URL = 'https://api.brevo.com/v3'

const getConfig = (overrides = {}) => {
  const apiKey = (overrides.apiKey ?? getEnvValue('VITE_BREVO_API_KEY') ?? '').trim()
  const baseUrl = (overrides.baseUrl ?? getEnvValue('VITE_BREVO_API_URL') ?? DEFAULT_BASE_URL).replace(/\/$/, '')

  return { apiKey, baseUrl }
}

export const isBrevoConfigured = (overrides = {}) => {
  const { apiKey } = getConfig(overrides)
  return Boolean(apiKey)
}

const buildHeaders = (config) => ({
  'Content-Type': 'application/json',
  'api-key': config.apiKey
})

const handleBrevoResponse = async (response) => {
  const text = await response.text()
  const data = text ? JSON.parse(text) : null

  if (!response.ok) {
    const message = data?.message || data?.error || 'Brevo API request failed'
    const error = new Error(message)
    error.status = response.status
    error.details = data
    throw error
  }

  return data
}

const normaliseRecipient = (recipient) => {
  if (!recipient) return null

  if (typeof recipient === 'string') {
    return { email: recipient }
  }

  return {
    email: recipient.email,
    name: recipient.name
  }
}

export const sendBrevoEmail = async ({
  subject,
  htmlContent,
  textContent,
  sender,
  to,
  replyTo,
  tags = [],
  headers = {},
  overrides = {}
}) => {
  const config = getConfig(overrides)

  if (!isBrevoConfigured(config)) {
    throw new Error('Brevo API is not configured')
  }

  const recipients = Array.isArray(to) ? to.map(normaliseRecipient).filter(Boolean) : [normaliseRecipient(to)].filter(Boolean)

  if (!recipients.length) {
    throw new Error('At least one valid recipient is required to send an email')
  }

  const payload = {
    subject,
    htmlContent,
    to: recipients
  }

  if (textContent) {
    payload.textContent = textContent
  }

  if (sender?.email) {
    payload.sender = sender
  }

  if (replyTo?.email) {
    payload.replyTo = replyTo
  }

  if (tags.length) {
    payload.tags = tags
  }

  if (Object.keys(headers).length) {
    payload.headers = headers
  }

  const response = await fetch(`${config.baseUrl}/smtp/email`, {
    method: 'POST',
    headers: buildHeaders(config),
    body: JSON.stringify(payload)
  })

  return handleBrevoResponse(response)
}

export default {
  isBrevoConfigured,
  sendBrevoEmail
}
